import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useModelSelector } from './ModelSelectorContext';
import { useChat } from './ChatContext';
import { useDataConnection } from './DataConnectionContext';
import { useUser } from './UserContext';
import { useChatUI } from './ChatUIContext';
import { CommandRegistry } from '../services/commands/CommandRegistry';
import { CommandHistoryService } from '../services/api/commandHistoryService';
import { CommandExecutionService } from '../services/api/commandExecutionService';
import { debounce } from '../utils/debounce';

export interface CommandDefinition {
  name: string;
  description: string;
  usage: string;
  examples: string[];
  category: 'data' | 'navigation' | 'analysis' | 'system' | 'ai';
  permissions: string[];
  handler: (args: string[], context: CommandContext) => Promise<CommandResult>;
}

export interface CommandResult {
  success: boolean;
  message: string;
  data?: any;
  cards?: Array<{
    type: string;
    artifactId?: string;
    artifact?: any;
    taskId?: string;
    task?: any;
    [key: string]: any;
  }>;
  suggestions?: string[];
  error?: string;
  metadata?: {
    executionTime: number;
    dataSource: string;
    environment: string;
  };
}

export interface CommandContext {
  uiRole: string;
  environment: string;
  dataSource: string;
  artifactLevel: string;
  selectedArtifactId?: string;
  currentThreadId?: string | null;
  // Thread context for filtering commands - now includes full model context from backend
  threadModelContext?: {
    thread_id: string;
    thread_name: string;
    ui_role: string;
    model_type: 'prime' | 'company' | 'unknown' | 'none';
    prime_artifact_id?: string;
    artifact_tree_id?: string;
    model_display_name: string;
    model: any;
    context: any;
    user_id: string;
  };
  // ChatUI context - used as fallback when thread context is missing
  chatUIModelContext?: {
    model_type: 'prime' | 'company' | 'unknown' | 'none';
    prime_artifact_id?: string;
    artifact_tree_id?: string;
    ui_role: string;
    model: any;
  };
  // Legacy fields for backward compatibility
  currentThreadModel?: {
    type: 'prime' | 'company';
    artifactId?: string; // For Prime model
    artifactTreeId?: string; // For Company model
  };
  currentThreadContext?: {
    selectedArtifactId?: string;
    selectedTaskId?: string;
    environment?: string;
  };
}

export interface CommandState {
  availableCommands: CommandDefinition[];
  commandHistory: string[];
  isExecuting: boolean;
  lastCommand?: string;
  lastResult?: CommandResult;
  error: string | null;
  suggestions: string[];
}

interface CommandContextType extends CommandState {
  // UI State
  selectedCommand?: string;
  showCommandHelp: boolean;
  setShowCommandHelp: (show: boolean) => void;
  
  // Command Execution
  executeCommand: (input: string) => Promise<CommandResult>;
  executeSlashCommand: (command: string, args: string[]) => Promise<CommandResult>;
  
  // Command Management
  registerCommand: (command: CommandDefinition) => void;
  unregisterCommand: (commandName: string) => void;
  getCommand: (name: string) => CommandDefinition | undefined;
  
  // Command Discovery
  getAvailableCommands: (category?: string) => CommandDefinition[];
  getCommandSuggestions: (input: string) => string[];
  getCommandHelp: (commandName: string) => string;
  
  // History Management
  addToHistory: (command: string) => void;
  clearHistory: () => void;
  getHistory: () => string[];
  
  // AI Integration
  processNaturalLanguage: (input: string) => Promise<CommandResult>;
  generateCommandFromIntent: (intent: string) => Promise<string>;
  
  // Error Handling
  handleCommandError: (error: Error, command: string) => void;
  clearError: () => void;
}

// eslint-disable-next-line no-redeclare
const CommandContext = createContext<CommandContextType | undefined>(undefined);

// Built-in command definitions
// const BUILT_IN_COMMANDS: CommandDefinition[] = [
//   {
//     name: 'items',
//     description: 'Show items for a specific artifact',
//     usage: '/items <artifact_name>',
//     examples: ['/items Operating Checking', '/items financial_account'],
//     category: 'data',
//     permissions: ['read:artifacts'],
//     handler: async (args, _context) => {
//       const artifactName = args.join(' ');
//       // Implementation would fetch and display artifact items
//       return {
//         success: true,
//         message: `Showing items for ${artifactName}`,
//         data: { artifactName },
//         suggestions: ['/map', '/process', '/validate'],
//       };
//     },
//   },
//   {
//     name: 'map',
//     description: 'Display state map for an artifact type',
//     usage: '/map <artifact_type>',
//     examples: ['/map financial_account', '/map process'],
//     category: 'data',
//     permissions: ['read:artifacts'],
//     handler: async (args, _context) => {
//       const artifactType = args[0];
//       return {
//         success: true,
//         message: `Showing state map for ${artifactType}`,
//         data: { artifactType },
//         suggestions: ['/items', '/process', '/validate'],
//       };
//     },
//   },
//   {
//     name: 'process',
//     description: 'Show detailed process information',
//     usage: '/process <process_name>',
//     examples: ['/process Treasury: Reconcile Accounts', '/process Invoice Processing'],
//     category: 'data',
//     permissions: ['read:processes'],
//     handler: async (args, _context) => {
//       const processName = args.join(' ');
//       return {
//         success: true,
//         message: `Showing process details for ${processName}`,
//         data: { processName },
//         suggestions: ['/items', '/map', '/validate'],
//       };
//     },
//   },
//   {
//     name: 'validate',
//     description: 'Run validation checks',
//     usage: '/validate [scope]',
//     examples: ['/validate', '/validate artifacts', '/validate processes'],
//     category: 'analysis',
//     permissions: ['read:artifacts', 'read:processes'],
//     handler: async (args, _context) => {
//       const scope = args[0] || 'all';
//       return {
//         success: true,
//         message: `Running validation for ${scope}`,
//         data: { scope },
//         suggestions: ['/items', '/map', '/process'],
//       };
//     },
//   },
//   {
//     name: 'help',
//     description: 'Show available commands and help',
//     usage: '/help [command]',
//     examples: ['/help', '/help items', '/help map'],
//     category: 'system',
//     permissions: [],
//     handler: async (args, _context) => {
//       const commandName = args[0];
//       return {
//         success: true,
//         message: commandName ? `Help for ${commandName}` : 'Available commands',
//         data: { commandName },
//         suggestions: ['/items', '/map', '/process', '/validate'],
//       };
//     },
//   },
//   {
//     name: 'clear',
//     description: 'Clear the current chat',
//     usage: '/clear',
//     examples: ['/clear'],
//     category: 'system',
//     permissions: [],
//     handler: async (_args, _context) => {
//       return {
//         success: true,
//         message: 'Chat cleared',
//         data: {},
//         suggestions: ['/help', '/items', '/map'],
//       };
//     },
//   },
// ];

export const CommandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { uiRole, environment, dataSource, artifactLevel } = useModelSelector();
  const { 
    artifacts, 
    processes, 
    states, 
    transitions,
    fetchArtifacts, 
    fetchProcesses, 
    fetchStates,
  } = useDataConnection();
  const { 
    currentThreadId,
    threads,
    createThread,
    selectThread,
    deleteThread,
  } = useChat();
  const { user, isAuthenticated } = useUser();
  const { model: chatUIModel, uiRole: chatUIRole } = useChatUI();
  
  const [state, setState] = useState<CommandState>({
    availableCommands: [],
    commandHistory: [],
    isExecuting: false,
    error: null,
    suggestions: [],
  });

  // UI state
  const [selectedCommand] = useState<string | undefined>(undefined);
  const [showCommandHelp, setShowCommandHelp] = useState(false);
  const [pinnedArtifacts, setPinnedArtifacts] = useState<any[]>([]);

  const commandRegistry = useRef<CommandRegistry>(CommandRegistry.getInstance());
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Inject dependencies into command registry (debounced to prevent excessive updates)
  useEffect(() => {
    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Debounce the dependency update by 300ms
    updateTimeoutRef.current = setTimeout(() => {
      // Removed verbose log - dependencies are updated
      commandRegistry.current.setDependencies({
        artifacts,
        processes,
        states,
        transitions,
        fetchArtifacts,
        fetchProcesses,
        fetchStates,
        threads,
        createThread,
        switchThread: selectThread,
        deleteThread,
        pinnedArtifacts,
        pinArtifact: async (artifactId: string) => {
          const artifact = artifacts.find((a: any) => a.id === artifactId);
          if (artifact && !pinnedArtifacts.find((p: any) => p.id === artifactId)) {
          setPinnedArtifacts(prev => [...prev, artifact]);
        }
      },
        unpinArtifact: async (artifactId: string) => {
          setPinnedArtifacts(prev => prev.filter((p: any) => p.id !== artifactId));
        },
      });
    }, 300);
    
    // Cleanup timeout on unmount or dependency change
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [artifacts, processes, states, transitions, fetchArtifacts, fetchProcesses, fetchStates, 
      threads, createThread, selectThread, deleteThread, pinnedArtifacts, environment, dataSource]);

  const executeCommand = useCallback(async (input: string): Promise<CommandResult> => {
    console.log('🎯 [CommandContext] Executing command:', input);
    setState(prev => ({ ...prev, isExecuting: true, error: null }));
    
    const startTime = Date.now();
    
    try {
      // Check if it's a slash command
      if (input.startsWith('/')) {
        const [commandName, ...args] = input.slice(1).split(' ');
        console.log('🎯 [CommandContext] Slash command detected:', { commandName, args });
        const result = await executeSlashCommand(commandName, args);
        console.log('✅ [CommandContext] Command result:', result);
        return result;
      } else {
        // Process as natural language
        console.log('💬 [CommandContext] Processing as natural language');
        return await processNaturalLanguage(input);
      }
    } catch (error: any) {
      console.error('❌ [CommandContext] Command execution error:', error);
      const result: CommandResult = {
        success: false,
        message: 'Command execution failed',
        error: error.message,
        metadata: {
          executionTime: Date.now() - startTime,
          dataSource,
          environment,
        },
      };
      
      handleCommandError(error, input);
      return result;
    } finally {
      setState(prev => ({ 
        ...prev, 
        isExecuting: false,
        lastCommand: input,
      }));
    }
  }, [dataSource, environment, currentThreadId, user]);

  const executeSlashCommand = useCallback(async (commandName: string, args: string[]): Promise<CommandResult> => {
    console.log('🔍 [CommandContext] Looking up command:', commandName);
    const command = commandRegistry.current.getCommand(commandName);
    
    if (!command) {
      console.warn('⚠️ [CommandContext] Command not found:', commandName);
      return {
        success: false,
        message: `Unknown command: ${commandName}`,
        error: `Command '${commandName}' not found`,
        suggestions: getCommandSuggestions(commandName),
      };
    }

    console.log('✓ [CommandContext] Command found:', command.name, command.description);

    // Check permissions
    if (command.permissions.length > 0) {
      console.log('🔐 [CommandContext] Command requires permissions:', command.permissions);
      // In a real implementation, check user permissions here
      // For now, we'll assume all permissions are granted
    }

    // Check if we have a thread ID for command execution
    console.log('🔍 [CommandContext] Thread ID check:', { currentThreadId, dataSource, user: user?.id });
    if (!currentThreadId) {
      console.warn('⚠️ [CommandContext] No current thread ID available for command execution');
      return {
        success: false,
        message: 'No thread selected. Please select a thread before executing commands.',
        error: 'No thread context available',
        suggestions: ['/help', '/threads'],
      };
    }

    // For live data, execute command on backend with thread context
    if (dataSource === 'live' && user?.id) {
      try {
        console.log('🚀 [CommandContext] Executing command on backend:', { commandName, args, currentThreadId });
        
        const response = await CommandExecutionService.executeCommand(
          commandName,
          args,
          user.id,
          currentThreadId
        );
        
        console.log('✅ [CommandContext] Backend command execution result:', response);
        
        if (response.success) {
          // Extract the actual command result from the backend response
          const resultData = response.result || {};
          const commandMessage = resultData.message || resultData.type || 'Command executed successfully';
          
          return {
            success: true,
            message: commandMessage,
            data: response.result,
            metadata: {
              executionTime: response.execution_time,
              dataSource,
              environment,
            },
          };
        } else {
          return {
            success: false,
            message: response.error || 'Command execution failed',
            error: response.error,
            suggestions: getCommandSuggestions(commandName),
          };
        }
      } catch (error) {
        console.error('❌ [CommandContext] Backend command execution failed:', error);
        return {
          success: false,
          message: `Command execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          error: error instanceof Error ? error.message : String(error),
          suggestions: getCommandSuggestions(commandName),
        };
      }
    }

    // For sample data, execute command locally (existing logic)
    console.log('📊 [CommandContext] Using sample data - executing command locally');
    
    // Debug thread and model information
    console.log('🔍 [CommandContext] Thread debugging:');
    console.log('  - currentThreadId:', currentThreadId);
    console.log('  - using sample data mode');
    
    // Build ChatUI model context as fallback
    const chatUIModelContext = chatUIModel ? {
      model_type: (chatUIModel.type === 'prime' ? 'prime' : chatUIModel.type === 'company' ? 'company' : 'unknown') as 'prime' | 'company' | 'unknown',
      prime_artifact_id: chatUIModel.type === 'prime' ? chatUIModel.artifactId : undefined,
      artifact_tree_id: chatUIModel.type === 'company' ? chatUIModel.artifactTreeId : undefined,
      ui_role: chatUIRole,
      model: chatUIModel,
    } : undefined;

    const context: CommandContext = {
      uiRole: chatUIRole || uiRole,
      environment,
      dataSource,
      artifactLevel,
      selectedArtifactId: undefined, // No selected artifact for sample data
      currentThreadId,
      threadModelContext: undefined, // No thread context for sample data
      chatUIModelContext, // Use ChatUI context as fallback
      // Legacy fields for backward compatibility
      currentThreadModel: chatUIModel ? {
        type: chatUIModel.type === 'prime' ? 'prime' : 'company',
        artifactId: chatUIModel.artifactId,
        artifactTreeId: chatUIModel.artifactTreeId,
      } : undefined,
      currentThreadContext: {
        selectedArtifactId: undefined,
        environment,
      },
    };

    console.log('📋 [CommandContext] Command context:', context);
    console.log('📋 [CommandContext] Using sample data context:', context.threadModelContext);

    const startTime = Date.now();
    
    try {
      console.log('⚡ [CommandContext] Executing command handler...');
      const result = await command.handler(args, context);
      console.log('✅ [CommandContext] Command handler completed:', {
        success: result.success,
        hasCards: !!result.cards,
        cardCount: result.cards?.length || 0,
        hasSuggestions: !!result.suggestions,
      });
      
      // Add metadata
      result.metadata = {
        executionTime: Date.now() - startTime,
        dataSource,
        environment,
      };

      // Add to local history
      addToHistory(`/${commandName} ${args.join(' ')}`);
      
      // Save to backend if authenticated and in live mode
      if (isAuthenticated && user && dataSource === 'live') {
        try {
          console.log('🔍 [CommandContext.executeSlashCommand] Saving command history to backend');
          await CommandHistoryService.saveCommandHistory({
            user_id: user.id,
            command: commandName,
            args: args,
            result: result,
            execution_time: Date.now() - startTime,
            success: result.success,
            error_message: result.error || undefined,
            thread_model_context: undefined, // No thread context for sample data
          });
          console.log('✅ [CommandContext.executeSlashCommand] Command history saved');
        } catch (error) {
          console.error('❌ [CommandContext.executeSlashCommand] Failed to save command history:', error);
        }
      }
      
      setState(prev => ({
        ...prev,
        lastResult: result,
        suggestions: result.suggestions || [],
      }));

      return result;
    } catch (error: any) {
      console.error('❌ [CommandContext] Command handler error:', error);
      
      // Save failed command to backend if authenticated and in live mode
      if (isAuthenticated && user && dataSource === 'live') {
        try {
          console.log('🔍 [CommandContext.executeSlashCommand] Saving failed command history to backend');
          await CommandHistoryService.saveCommandHistory({
            user_id: user.id,
            command: commandName,
            args: args,
            result: undefined,
            execution_time: Date.now() - startTime,
            success: false,
            error_message: error.message,
            thread_model_context: undefined, // No thread context for sample data
          });
          console.log('✅ [CommandContext.executeSlashCommand] Failed command history saved');
        } catch (historyError) {
          console.error('❌ [CommandContext.executeSlashCommand] Failed to save failed command history:', historyError);
        }
      }
      
      throw new Error(`Command execution failed: ${error.message}`);
    }
  }, [uiRole, environment, dataSource, artifactLevel, currentThreadId, isAuthenticated, user, threads]);

  const processNaturalLanguage = useCallback(async (input: string): Promise<CommandResult> => {
    // In a real implementation, this would use AI/NLP to understand intent
    // For now, we'll provide a simple response
    
    const startTime = Date.now();
    
    try {
      // Simple keyword matching for demo
      const lowerInput = input.toLowerCase();
      
      if (lowerInput.includes('show') && lowerInput.includes('items')) {
        const artifactMatch = input.match(/show items for (.+)/i);
        if (artifactMatch) {
          return await executeSlashCommand('items', [artifactMatch[1]]);
        }
      }
      
      if (lowerInput.includes('state map') || lowerInput.includes('map')) {
        const typeMatch = input.match(/map for (.+)/i) || input.match(/state map for (.+)/i);
        if (typeMatch) {
          return await executeSlashCommand('map', [typeMatch[1]]);
        }
      }
      
      if (lowerInput.includes('process')) {
        const processMatch = input.match(/process (.+)/i);
        if (processMatch) {
          return await executeSlashCommand('process', [processMatch[1]]);
        }
      }
      
      if (lowerInput.includes('validate') || lowerInput.includes('check')) {
        return await executeSlashCommand('validate', []);
      }
      
      // Default response for unrecognized natural language
      return {
        success: true,
        message: `I understand you want to: "${input}". Try using slash commands like /help for available options.`,
        suggestions: ['/help', '/items', '/map', '/process', '/validate'],
        metadata: {
          executionTime: Date.now() - startTime,
          dataSource,
          environment,
        },
      };
    } catch (error: any) {
      throw new Error(`Natural language processing failed: ${error.message}`);
    }
  }, [dataSource, environment, executeSlashCommand]);

  const registerCommand = useCallback((command: CommandDefinition) => {
    commandRegistry.current.registerCommand(command);
    setState(prev => ({
      ...prev,
      availableCommands: commandRegistry.current.getAllCommands(),
    }));
  }, []);

  const unregisterCommand = useCallback((_commandName: string) => {
    // Note: CommandRegistry doesn't have unregister method, but we can implement it if needed
    setState(prev => ({
      ...prev,
      availableCommands: commandRegistry.current.getAllCommands(),
    }));
  }, []);

  const getCommand = useCallback((name: string) => {
    return commandRegistry.current.getCommand(name);
  }, []);

  const getAvailableCommands = useCallback((category?: string) => {
    return category ? commandRegistry.current.getCommandsByCategory(category) : commandRegistry.current.getAllCommands();
  }, []);

  const getCommandSuggestions = useCallback((input: string) => {
    const commands = commandRegistry.current.searchCommands(input);
    
    return commands
      .map(cmd => `/${cmd.name}`)
      .slice(0, 5);
  }, []);

  const getCommandHelp = useCallback((commandName: string) => {
    const command = commandRegistry.current.getCommand(commandName);
    if (!command) {
      return `Command '${commandName}' not found. Use /help to see available commands.`;
    }
    
    return `${command.description}\nUsage: ${command.usage}\nExamples: ${command.examples.join(', ')}`;
  }, []);

  const addToHistory = useCallback((command: string) => {
    setState(prev => ({
      ...prev,
      commandHistory: [...prev.commandHistory.slice(-49), command], // Keep last 50 commands
    }));
  }, []);

  const clearHistory = useCallback(() => {
    setState(prev => ({ ...prev, commandHistory: [] }));
  }, []);

  const getHistory = useCallback(() => {
    return state.commandHistory;
  }, [state.commandHistory]);

  const generateCommandFromIntent = useCallback(async (intent: string): Promise<string> => {
    // In a real implementation, this would use AI to generate appropriate commands
    // For now, return a simple mapping
    
    const lowerIntent = intent.toLowerCase();
    
    if (lowerIntent.includes('show') && lowerIntent.includes('items')) {
      return '/items';
    } else if (lowerIntent.includes('map') || lowerIntent.includes('state')) {
      return '/map';
    } else if (lowerIntent.includes('process')) {
      return '/process';
    } else if (lowerIntent.includes('validate') || lowerIntent.includes('check')) {
      return '/validate';
    } else {
      return '/help';
    }
  }, []);

  const handleCommandError = useCallback((error: Error, command: string) => {
    console.error('Command error:', error);
    setState(prev => ({
      ...prev,
      error: `${command}: ${error.message}`,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: CommandContextType = useMemo(() => ({
    ...state,
    // UI state
    selectedCommand,
    showCommandHelp,
    setShowCommandHelp,
    // Command execution
    executeCommand,
    executeSlashCommand,
    // Command management
    registerCommand,
    unregisterCommand,
    getCommand,
    // Command discovery
    getAvailableCommands,
    getCommandSuggestions,
    getCommandHelp,
    // History management
    addToHistory,
    clearHistory,
    getHistory,
    // AI integration
    processNaturalLanguage,
    generateCommandFromIntent,
    // Error handling
    handleCommandError,
    clearError,
  }), [
    state,
    selectedCommand,
    showCommandHelp,
    setShowCommandHelp,
    executeCommand,
    executeSlashCommand,
    registerCommand,
    unregisterCommand,
    getCommand,
    getAvailableCommands,
    getCommandSuggestions,
    getCommandHelp,
    addToHistory,
    clearHistory,
    getHistory,
    processNaturalLanguage,
    generateCommandFromIntent,
    handleCommandError,
    clearError,
  ]);

  return (
    <CommandContext.Provider value={value}>
      {children}
    </CommandContext.Provider>
  );
};

export const useCommands = (): CommandContextType => {
  const context = useContext(CommandContext);
  if (context === undefined) {
    throw new Error('useCommands must be used within a CommandProvider');
  }
  return context;
};
